import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';
import type { Asset, Entry } from 'contentful';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { SweetpassLandingPage } from './SweetpassLandingPage';

// ─────────────────────────────────────────────────────────────────────────────

export const useSweetpassLandingPageContent = (
  props: UseSweetpassLandingPageProps,
) => {
  const { tag, pause, contentFallback, onCtaPress } = props;

  const { data, error, fetching } =
    useContentfulContentTypeEntry<SweetpassLandingPageData>({
      contentType: 'sweetpassUpgradePage',
      include: 2,
      pause: pause || !tag,
      tags: tag ? [tag] : [],
    });

  const {
    header: headerContentFallback,
    benefits: benefitsContentFallback,
    cta: ctaContentFallback,
  } = contentFallback ?? {};

  const hasData = data !== undefined;

  // ─────────────────────────────────────────────────────────────────────

  const maybeHeaderContentFields = getContentFields<
    SweetpassLandingPageHeaderData['fields']
  >(data, 'sweetpassUpgradeHeader');
  const maybeBenefitsContentFields = getContentFields<
    SweetpassLandingPageBenefitsData['fields']
  >(data, 'sweetpassUpgradeBenefitList');
  const maybeCtaContentFields = data?.fields?.cta?.fields;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleHeaderCtaPress = useCallback(() => {
    onCtaPress(maybeHeaderContentFields?.cta.fields.action);
  }, [maybeHeaderContentFields?.cta.fields.action, onCtaPress]);

  const handleCtaPress = useCallback(() => {
    onCtaPress(maybeCtaContentFields?.action);
  }, [maybeCtaContentFields?.action, onCtaPress]);

  // ─── Derived Data ────────────────────────────────────────────────────

  const headerContent = useMemo<
    SweetpassLandingPageProps['headerContent']
  >(() => {
    if (!maybeHeaderContentFields) return headerContentFallback;

    // ─────────────────────────────────────────────

    const { palette, headingTextV2, bodyText, heroImage, cta } =
      maybeHeaderContentFields;

    const title = headingTextV2;
    const text = bodyText;
    const img = heroImage;
    const imgUrl = img.fields.file.url;
    const imgA11yLabel = img.fields.description;
    const ctaLabel = cta.fields.title;

    // ─────────────────────────────────────────────

    return {
      title,
      text,
      imgUrl,
      imgA11yLabel,
      palette,
      ctaLabel,
      onCtaPress: handleHeaderCtaPress,
    };
  }, [handleHeaderCtaPress, headerContentFallback, maybeHeaderContentFields]);

  const benefitsContent = useMemo<
    SweetpassLandingPageProps['benefitsContent']
  >(() => {
    if (!maybeBenefitsContentFields) return benefitsContentFallback;

    // ─────────────────────────────────────────────

    const { benefitList, itemsPerRow: itemsPerRowMD } =
      maybeBenefitsContentFields;
    const benefits = benefitList.map(getBenefit);

    // ─────────────────────────────────────────────

    return { benefits, itemsPerRowMD };
  }, [benefitsContentFallback, maybeBenefitsContentFields]);

  const ctaContent = useMemo<SweetpassLandingPageProps['ctaContent']>(() => {
    if (!maybeCtaContentFields) return ctaContentFallback;

    // ─────────────────────────────────────────────

    const { title: label, palette, action } = maybeCtaContentFields;

    return { label, palette, onPress: handleCtaPress, action };
  }, [ctaContentFallback, handleCtaPress, maybeCtaContentFields]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    fetching,
    error,
    hasContent: hasData,
    content: {
      header: headerContent,
      benefits: benefitsContent,
      cta: ctaContent,
    },
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getContentFields<ContentType>(
  data: MaybeContentfulSweetpassLandingPageDate,
  contentId: string,
): ContentType | undefined {
  const { content } = data?.fields ?? {};

  return content?.find(
    (contentEntry) => getContentTypeId(contentEntry) === contentId,
  )?.fields as ContentType | undefined;
}

function getContentTypeId(contentEntry: SweetpassLandingPageDataContent) {
  return contentEntry.sys.contentType.sys.id;
}

function getBenefit(
  benefit: SweetpassLandingPageBenefitsData['fields']['benefitList'][number],
) {
  const { fields, sys } = benefit;

  // ─────────────────────────────────────────────

  const { id } = sys;
  const { titleText: title, bodyText: text, image } = fields;
  const imgUrl = image.fields.file.url;
  const imgDescription = image.fields.description;

  // ─────────────────────────────────────────────

  return {
    id,
    title,
    text,
    image: { url: imgUrl, description: imgDescription },
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassLandingPageProps = {
  readonly pause: boolean;
  readonly tag?: string;
  readonly onCtaPress: (action?: SweetpassLandingPageCtaAction) => void;
  readonly contentFallback?: {
    readonly header: SweetpassLandingPageProps['headerContent'];
    readonly benefits: SweetpassLandingPageProps['benefitsContent'];
    readonly cta: SweetpassLandingPageProps['ctaContent'];
  };
};

type SweetpassLandingPageData = {
  readonly content: readonly SweetpassLandingPageDataContent[];
  readonly cta: Entry<{
    readonly palette: SweetpassLandingPagePalette;
    readonly title: string;
    readonly action: SweetpassLandingPageCtaAction;
  }>;
};

// ─────────────────────────────────────────────────────────────────────────────

type MaybeContentfulSweetpassLandingPageDate = ReturnType<
  typeof useContentfulContentTypeEntry<SweetpassLandingPageData>
>['data'];

type SweetpassLandingPageDataContent =
  | SweetpassLandingPageHeaderData
  | SweetpassLandingPageBenefitsData;

type SweetpassLandingPageHeaderData = Entry<{
  readonly bodyText: string;
  readonly cta: Entry<{
    readonly palette: SweetpassLandingPagePalette;
    readonly title: string;
    readonly action: SweetpassLandingPageCtaAction;
  }>;
  readonly headingText: string;
  readonly headingTextV2: string;
  readonly heroImage: Asset;
  readonly palette: SweetpassLandingPagePalette;
}>;

type SweetpassLandingPageBenefitsData = Entry<{
  readonly itemsPerRow: number | undefined;
  readonly benefitList: readonly SweetpassLandingPageBenefit[];
}>;

type SweetpassLandingPageBenefit = Entry<{
  readonly image: Asset;
  readonly titleText: string;
  readonly bodyText: string;
}>;

export type SweetpassLandingPageCtaAction =
  | 'join_sweetpass'
  | 'upgrade_to_sweetpass_plus';

type SweetpassLandingPagePalette = 'kale' | 'lime';

type SweetpassLandingPageProps = ComponentProps<typeof SweetpassLandingPage>;
